import React, { useMemo, useState } from 'react';
import {
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Label,
  Title,
} from '@patternfly/react-core';
import { ExpandableRowContent, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { useMollieInvoices } from '../../../hooks/useAdminReconciliation';
import type { MollieInvoice } from '../../../types/models/Reconciliation';
import { formatBareDateNumeric, formatInstantBrussels, formatMonthYear } from '../../../utils/dateFormat';
import {
  AMOUNT_CELL,
  CheckLabel,
  CsvButton,
  EmptyListState,
  ExactMoney,
  FetchedAt,
  ListPagination,
  ListToolbar,
  LiveReadSpinner,
  Money,
  NoMatchState,
  ReadErrorState,
  ReconciliationBanners,
  RefreshButton,
  SettlementRef,
  StaleDataAlert,
  YearPicker,
  matchesQuery,
  useClientList,
} from './ReconciliationParts';
import { MOLLIE_INVOICE_STATUS_LABELS, labelOf } from './reconciliationLabels';
import { MollieInvoicePdfLink } from './ReconciliationOverviewTab';
import { buildMollieInvoicesCsv, reconciliationCsvFilename } from './reconciliationCsv';

const matchInvoice = (inv: MollieInvoice, q: string) =>
  matchesQuery(q, [inv.reference, inv.id, inv.periods, inv.periods.map(formatMonthYear)]);

const COLUMN_COUNT = 9;

const InvoiceDetails: React.FunctionComponent<{ invoice: MollieInvoice }> = ({ invoice }) => {
  const label = invoice.reference ?? invoice.id ?? '';
  return (
    <Grid hasGutter>
      <GridItem lg={6}>
        <Title headingLevel="h4" size="md" className="pf-v6-u-mb-sm">
          Lignes par moyen de paiement (comme sur le PDF)
        </Title>
        <Table aria-label={`Lignes par moyen de paiement de la facture ${label}`} variant="compact">
          <Thead>
            <Tr>
              <Th>Moyen de paiement</Th>
              <Th className={AMOUNT_CELL}>Montant HTVA</Th>
            </Tr>
          </Thead>
          <Tbody>
            {invoice.lines_by_method.map((l, i) => (
              <Tr key={`${l.description}-${i}`}>
                <Td dataLabel="Moyen de paiement">{l.description ?? '—'}</Td>
                <Td dataLabel="Montant HTVA" className={AMOUNT_CELL}>
                  <Money value={l.amount} />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </GridItem>
      <GridItem lg={6}>
        <Title headingLevel="h4" size="md" className="pf-v6-u-mb-sm">
          Rapprochement avec les versements
        </Title>
        <DescriptionList isCompact isHorizontal>
          <DescriptionListGroup>
            <DescriptionListTerm>Retenu par les versements</DescriptionListTerm>
            <DescriptionListDescription>
              <Money value={invoice.reconciliation.withheld} />
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Compensations</DescriptionListTerm>
            <DescriptionListDescription>
              <Money value={invoice.reconciliation.compensations} />
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Retenu net</DescriptionListTerm>
            <DescriptionListDescription>
              <Money value={invoice.reconciliation.settled_total} />
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Facturé</DescriptionListTerm>
            <DescriptionListDescription>
              <Money value={invoice.reconciliation.invoiced} />
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Résultat</DescriptionListTerm>
            <DescriptionListDescription>
              <CheckLabel ok={invoice.reconciliation.ok} difference={invoice.reconciliation.difference} />
            </DescriptionListDescription>
          </DescriptionListGroup>
          {invoice.pdf_expires_at && (
            <DescriptionListGroup>
              <DescriptionListTerm>Lien PDF valable jusqu&apos;au</DescriptionListTerm>
              <DescriptionListDescription>{formatInstantBrussels(invoice.pdf_expires_at)}</DescriptionListDescription>
            </DescriptionListGroup>
          )}
        </DescriptionList>
      </GridItem>
      <GridItem span={12}>
        <Title headingLevel="h4" size="md" className="pf-v6-u-mb-sm">
          Versements qui ont prépayé cette facture
        </Title>
        {invoice.settled_through.length === 0 ? (
          <EmptyListState title="Aucun versement" />
        ) : (
          <Table aria-label={`Versements qui ont prépayé la facture ${label}`} variant="compact">
            <Thead>
              <Tr>
                <Th>Versement</Th>
                <Th>Versé le</Th>
                <Th>Période</Th>
                <Th className={AMOUNT_CELL}>Frais</Th>
                <Th className={AMOUNT_CELL}>Arrondi</Th>
                <Th className={AMOUNT_CELL}>Compensation</Th>
                <Th className={AMOUNT_CELL}>Remise</Th>
                <Th className={AMOUNT_CELL}>Autre</Th>
                <Th className={AMOUNT_CELL}>Total</Th>
              </Tr>
            </Thead>
            <Tbody>
              {invoice.settled_through.map((e) => (
                <Tr key={`${e.settlement_id}-${e.period}`}>
                  <Td dataLabel="Versement">
                    <SettlementRef id={e.settlement_id} reference={e.settlement_reference} />
                  </Td>
                  <Td dataLabel="Versé le">{formatInstantBrussels(e.settled_at)}</Td>
                  <Td dataLabel="Période">{formatMonthYear(e.period)}</Td>
                  <Td dataLabel="Frais" className={AMOUNT_CELL}>
                    <Money value={e.fees} />
                  </Td>
                  <Td dataLabel="Arrondi" className={AMOUNT_CELL}>
                    <Money value={e.rounding} />
                  </Td>
                  <Td dataLabel="Compensation" className={AMOUNT_CELL}>
                    <Money value={e.compensation} />
                  </Td>
                  <Td dataLabel="Remise" className={AMOUNT_CELL}>
                    <Money value={e.discount} />
                  </Td>
                  <Td dataLabel="Autre" className={AMOUNT_CELL}>
                    <Money value={e.other} />
                  </Td>
                  <Td dataLabel="Total" className={AMOUNT_CELL}>
                    <Money value={e.total} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </GridItem>
      <GridItem span={12}>
        <Title headingLevel="h4" size="md" className="pf-v6-u-mb-sm">
          Lignes détaillées de l&apos;API Mollie (montants exacts, en anglais)
        </Title>
        <Table aria-label={`Lignes détaillées de la facture ${label}`} variant="compact">
          <Thead>
            <Tr>
              <Th>Période</Th>
              <Th>Description</Th>
              <Th className={AMOUNT_CELL}>Nombre</Th>
              <Th className={AMOUNT_CELL}>Montant HTVA</Th>
            </Tr>
          </Thead>
          <Tbody>
            {invoice.lines.map((l, i) => (
              <Tr key={`${l.period}-${l.description}-${i}`}>
                <Td dataLabel="Période">{formatMonthYear(l.period)}</Td>
                <Td dataLabel="Description">{l.description ?? '—'}</Td>
                <Td dataLabel="Nombre" className={AMOUNT_CELL}>
                  {l.count ?? '—'}
                </Td>
                <Td dataLabel="Montant HTVA" className={AMOUNT_CELL}>
                  <ExactMoney value={l.amount} />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </GridItem>
    </Grid>
  );
};

interface MollieInvoicesTabProps {
  year: number;
  onYearChange: (year: number) => void;
  isActive: boolean;
}

/**
 * Factures Mollie — `GET /admin/reconciliation/mollie/invoices`: Mollie's own monthly fee invoices to
 * Ancalagon (reverse charge, fully prepaid), each reconciled through the payouts that withheld its fees.
 */
const MollieInvoicesTab: React.FunctionComponent<MollieInvoicesTabProps> = ({ year, onYearChange, isActive }) => {
  const { data, loading, error, fetchedAt, refetch } = useMollieInvoices(year, isActive);
  const rows = useMemo(() => data?.invoices ?? [], [data]);
  const list = useClientList(rows, matchInvoice);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const header = (
    <Flex
      alignItems={{ default: 'alignItemsCenter' }}
      spaceItems={{ default: 'spaceItemsMd' }}
      className="pf-v6-u-mb-md"
    >
      <FlexItem>
        <YearPicker year={year} onChange={onYearChange} />
      </FlexItem>
      <FlexItem>
        <RefreshButton loading={loading} onRefresh={() => void refetch()} />
      </FlexItem>
      <FlexItem>
        <FetchedAt fetchedAt={fetchedAt} />
      </FlexItem>
    </Flex>
  );

  if (data === null) {
    return (
      <>
        {header}
        {error !== null && !loading ? (
          <ReadErrorState
            title="Impossible de charger les factures Mollie"
            message={error}
            onRetry={() => void refetch()}
          />
        ) : (
          <LiveReadSpinner what="factures Mollie" />
        )}
      </>
    );
  }

  return (
    <>
      {header}
      {error !== null && <StaleDataAlert message={error} />}
      <ReconciliationBanners warnings={data.warnings} onRetry={() => void refetch()} />
      <DescriptionList
        isCompact
        isHorizontal
        columnModifier={{ default: '1Col', md: '2Col' }}
        className="pf-v6-u-mb-md"
      >
        <DescriptionListGroup>
          <DescriptionListTerm>Factures émises en {year}</DescriptionListTerm>
          <DescriptionListDescription>{data.totals.count}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Total TVAC</DescriptionListTerm>
          <DescriptionListDescription>
            <strong>
              <Money value={data.totals.gross_amount} />
            </strong>
          </DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>
      <p className="pf-v6-u-mb-md pf-v6-u-text-color-subtle">
        Factures d&apos;achat Mollie (autoliquidation, TVA 0 %), entièrement prépayées par les frais retenus sur les
        versements. Le lien PDF est signé et expire : ouvrez-le, ne le conservez pas.
      </p>
      {rows.length === 0 ? (
        <EmptyListState title={`Aucune facture Mollie émise en ${year}`} />
      ) : (
        <>
          <ListToolbar
            list={list}
            placeholder="Rechercher par référence, identifiant ou période..."
            actions={
              <CsvButton
                label="Exporter l'année (CSV)"
                filename={reconciliationCsvFilename.mollieInvoices(year)}
                build={() => buildMollieInvoicesCsv(rows)}
              />
            }
          />
          {list.filtered.length === 0 ? (
            <NoMatchState onClear={() => list.setSearchValue('')} />
          ) : (
            <>
              <Table aria-label="Factures Mollie de l'année" variant="compact" isExpandable>
                <Thead>
                  <Tr>
                    <Th screenReaderText="Détails" />
                    <Th>Référence</Th>
                    <Th>Statut</Th>
                    <Th>Émise le</Th>
                    <Th>Payée le</Th>
                    <Th>Périodes</Th>
                    <Th className={AMOUNT_CELL}>TVAC</Th>
                    <Th>Rapprochement</Th>
                    <Th>PDF</Th>
                  </Tr>
                </Thead>
                {list.paginated.map((inv, rowIndex) => {
                  const key = inv.id ?? inv.reference ?? `inv-${rowIndex}`;
                  const isExpanded = expanded.has(key);
                  return (
                    <Tbody key={key} isExpanded={isExpanded}>
                      <Tr isContentExpanded={isExpanded}>
                        <Td
                          expand={{ rowIndex, isExpanded, onToggle: () => toggle(key), expandId: 'mollie-invoice-row' }}
                        />
                        <Td dataLabel="Référence" modifier="breakWord">
                          {inv.reference ?? inv.id ?? '—'}
                        </Td>
                        <Td dataLabel="Statut">
                          <Label
                            color={inv.status === 'paid' ? 'green' : inv.status === 'overdue' ? 'red' : 'blue'}
                            isCompact
                          >
                            {labelOf(MOLLIE_INVOICE_STATUS_LABELS, inv.status)}
                          </Label>
                        </Td>
                        <Td dataLabel="Émise le">{formatBareDateNumeric(inv.issued_at)}</Td>
                        <Td dataLabel="Payée le">{formatBareDateNumeric(inv.paid_at)}</Td>
                        <Td dataLabel="Périodes">{inv.periods.map(formatMonthYear).join(', ') || '—'}</Td>
                        <Td dataLabel="TVAC" className={AMOUNT_CELL}>
                          <Money value={inv.gross_amount} />
                        </Td>
                        <Td dataLabel="Rapprochement">
                          <CheckLabel ok={inv.reconciliation.ok} difference={inv.reconciliation.difference} />
                        </Td>
                        <Td dataLabel="PDF">
                          <MollieInvoicePdfLink invoice={inv} />
                        </Td>
                      </Tr>
                      <Tr isExpanded={isExpanded}>
                        <Td />
                        <Td colSpan={COLUMN_COUNT - 1}>
                          <ExpandableRowContent>
                            <InvoiceDetails invoice={inv} />
                          </ExpandableRowContent>
                        </Td>
                      </Tr>
                    </Tbody>
                  );
                })}
              </Table>
              <ListPagination list={list} variant="bottom" />
            </>
          )}
        </>
      )}
    </>
  );
};

export default MollieInvoicesTab;
